import { useCallback, useEffect, useState } from "react";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { storage } from "@data/firebase";
import { useAuth } from "@contexts/AuthContext";
import { useProjects } from "@contexts/ProjectContext";
import { projectStorageBasePath } from "@domain/project/paths";
import { invoiceRepository } from "@data/firestore";
import { callExtractInvoice } from "@data/functions/extractInvoice";

/**
 * Subscribes to invoices for the active project and exposes upload + extract
 * + save + delete operations.
 *
 * Lifecycle of an invoice:
 *  1. user drops PDF → `uploadPdf` puts it at projects/{pid}/invoices/{id}.pdf
 *     and returns { id, pdfStoragePath, pdfDownloadURL }
 *  2. caller invokes `extract({ id, pdfStoragePath })` which calls the
 *     onCall extractInvoice flow and returns { extraction, modelUsed, ... }
 *  3. user confirms fields → `save(invoice)` persists the full Invoice doc
 *  4. `remove(invoice)` deletes the PDF + the Firestore doc
 */
export default function useInvoices() {
  const { user } = useAuth();
  const { activeProject } = useProjects();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user || !activeProject) {
      setItems([]);
      setLoaded(true);
      return;
    }
    setLoaded(false);
    const unsub = invoiceRepository.watch(activeProject, (list) => {
      setItems(list);
      setLoaded(true);
    });
    return unsub;
  }, [user, activeProject]);

  const uploadPdf = useCallback(
    async (file) => {
      if (!user || !activeProject || !file) {
        throw new Error("Sign in and select a project first.");
      }
      const id =
        (crypto.randomUUID && crypto.randomUUID()) ||
        `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const path = `${projectStorageBasePath(activeProject)}/invoices/${id}.pdf`;
      const sref = storageRef(storage, path);
      await uploadBytes(sref, file, { contentType: "application/pdf" });
      const url = await getDownloadURL(sref);
      return {
        id,
        pdfStoragePath: path,
        pdfDownloadURL: url,
        pdfSize: file.size,
        pdfContentType: file.type || "application/pdf",
      };
    },
    [user, activeProject]
  );

  const extract = useCallback(
    async ({ pdfStoragePath }) => {
      if (!activeProject) throw new Error("No active project.");
      return callExtractInvoice({
        projectId: activeProject.id,
        pdfStoragePath,
      });
    },
    [activeProject]
  );

  const save = useCallback(
    async (invoice) => {
      if (!user || !activeProject) throw new Error("Sign in first.");
      await invoiceRepository.add(activeProject, invoice);
    },
    [user, activeProject]
  );

  const update = useCallback(
    async (invoiceId, patch) => {
      if (!activeProject) return;
      await invoiceRepository.update(activeProject, invoiceId, patch);
    },
    [activeProject]
  );

  const remove = useCallback(
    async (invoice) => {
      if (!user || !activeProject) return;
      try {
        await deleteObject(storageRef(storage, invoice.pdfStoragePath));
      } catch (err) {
        console.warn("[useInvoices] storage delete failed", err);
      }
      await invoiceRepository.remove(activeProject, invoice.id);
    },
    [user, activeProject]
  );

  // Delete a PDF that was uploaded but never saved as an invoice (draft
  // cancelled), so it doesn't sit orphaned in Storage.
  const discardUpload = useCallback(async (pdfStoragePath) => {
    if (!pdfStoragePath) return;
    try {
      await deleteObject(storageRef(storage, pdfStoragePath));
    } catch (err) {
      console.warn("[useInvoices] discard upload failed", err);
    }
  }, []);

  return {
    items,
    loaded,
    uploadPdf,
    discardUpload,
    extract,
    save,
    update,
    remove,
  };
}
